import { useCallback } from 'react'
import { useInView } from 'react-intersection-observer'
import { trackClientEvent } from 'analytics/analytics'
import type { ChooseDoubleRewardsChoice } from '../utils/loyaltyChooseDoubleRewardsState'
import { useLocation } from 'react-router-dom'
import { getPageCategoryAndType } from 'analytics/snowplow/pages'
import useIsOfferPage from 'hooks/Navigation/useIsOfferPage'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import UrlLocation from 'src/lib/url/UrlLocation'
import { CHOOSE_DOUBLE_REWARDS_SLUG } from '../loyaltyChooseDoubleRewardsTakeover.constants'

type Action = 'impression' | 'clicked' | 'sign_in_clicked' | 'join_clicked'
  | `option_selected_${ChooseDoubleRewardsChoice}`
  | `option_expanded_${ChooseDoubleRewardsChoice}`
  | `choice_confirmed_${ChooseDoubleRewardsChoice}`

function useLoyaltyChooseDoubleRewardsTracking(placement: string) {
  const { pathname } = useLocation()
  const isOfferPage = useIsOfferPage()

  const isLandingPage = matchLocaleAwarePath(pathname, { path: `/:region/societe/${CHOOSE_DOUBLE_REWARDS_SLUG}`, exact: true })

  const pageType = getPageCategoryAndType(new UrlLocation(pathname).withoutLocale().path).type

  let surface = 'other'
  switch (pageType) {
    case 'home': surface = 'homepage'; break
    case 'checkout':
    case 'inflow': surface = 'checkout'; break
    case 'search':
    case 'search_map': surface = 'search'; break
    case 'vertical': surface = 'vertical_page'; break
  }

  if (isOfferPage) surface = 'offer_page'
  if (isLandingPage) surface = 'landing'

  const track = useCallback((action: Action) => {
    trackClientEvent({
      category: 'societe_choose_double_rewards',
      subject: `${surface}:${placement}`,
      action,
      type: action === 'impression' ? 'nonInteraction' : 'interaction',
    })
  }, [surface, placement])

  const onChange = useCallback((inView: boolean) => {
    if (inView) track('impression')
  }, [track])

  const { ref: impressionRef } = useInView({ threshold: 0.5, triggerOnce: true, onChange })

  return { impressionRef, track }
}

export default useLoyaltyChooseDoubleRewardsTracking
